import { ProjectRenderer } from '../ProjectRenderer';
import { UUID } from '@/types/types';
import { RenderUtils } from '../utils/RenderUtils';
import { RendererMode } from '../types';
import {
	Vector2Tuple,
	CylinderGeometry,
	MeshBasicMaterial,
	Vector2,
	Group,
	Mesh,
} from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { DRAWING_VERTEX } = RENDERER_CONFIG;

export interface SerializedPlaygroundVertex {
	id: UUID;
	position: Vector2Tuple;
	prev: null | UUID;
	next: null | UUID;
}

const geometry = new CylinderGeometry(
	DRAWING_VERTEX.RADIUS,
	DRAWING_VERTEX.RADIUS,
	DRAWING_VERTEX.HEIGHT
);

const material = new MeshBasicMaterial({
	color: RENDERER_CONFIG.COLORS.BLACK,
});

export class DrawingVertex {
	private renderer: ProjectRenderer;
	private _position = new Vector2();
	private _isError = false;
	id = crypto.randomUUID();
	prev: null | DrawingVertex = null;
	next: null | DrawingVertex = null;
	group = new Group();
	isMovable = false;

	constructor(renderer: ProjectRenderer, position: Vector2Tuple) {
		this.renderer = renderer;

		this.position = new Vector2(position[0], position[1]);
		this.renderer.room.add(this.group);

		this.setupObject();
	}

	get isError() {
		return this._isError;
	}

	set isError(value: boolean) {
		this._isError = value;

		if (this.group.children[0])
			// @ts-ignore
			this.group.children[0].material.color.set(
				value ? RENDERER_CONFIG.COLORS.RED : RENDERER_CONFIG.COLORS.BLACK
			);
	}

	get position() {
		return this._position;
	}

	set position(pos: Vector2) {
		this._position = pos;
		this.group.position.set(pos.x, DRAWING_VERTEX.HEIGHT / 2, pos.y);

		for (const line of this.renderer.drawingManager.lines) {
			if (line.start === this || line.end === this) line.compute();
		}
	}

	private setupObject() {
		const mesh = new Mesh(geometry.clone(), material.clone());
		this.group.add(mesh);
		this.setObjectVisibility();
	}

	setObjectVisibility() {
		this.group.visible =
			this.renderer.mode === RendererMode.CUSTOM_SHAPE_DRAWING;
	}

	canConnectTo(slot: DrawingVertex) {
		if (this === slot) return false;
		if (this.prev && this.next) return false;
		if (slot.prev && slot.next) return false;
		return;
	}

	connectTo(slot: DrawingVertex) {
		if (!this.canConnectTo(slot)) return;

		this.prev = slot;
		slot.next = this;
	}

	disconnectSlots(slot: DrawingVertex) {}

	dispose() {
		RenderUtils.disposeObject(this.group);
	}
}
